import React, { ReactElement, useEffect, useRef, useState } from 'react';
import trackingService, { TrackingEvent } from '../../../Services/Tracking/TrackingService';
import IconButton from '../Buttons/IconButton';

export interface IIconMenuHorizontalDropdownOption {
    key: string;
    label: string;
    handleClick: () => void;
}

interface IIconMenuHorizontalDropdownProps {
    icon: ReactElement;
    options: IIconMenuHorizontalDropdownOption[];
    tooltip?: string;
    className?: string;
    containerClassName?: string;
    menuClassName?: string;
    itemClassName?: string;
    testId?: string;
    isOpen?: boolean;
    disabled?: boolean;
    onToggle?: (isOpen: boolean) => void;
    track: TrackingEvent;
}

const IconMenuHorizontalDropdown: React.FC<IIconMenuHorizontalDropdownProps> = (props) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownReference = useRef<HTMLDivElement>(null);

    const toggleDropdown = () => {
        const newIsOpen = !isOpen;
        setIsOpen(newIsOpen);
        props.onToggle?.(newIsOpen);

        trackingService.track({
            eventCategory: props.track.eventCategory,
            eventAction: `${props.track.eventAction}Toggled`,
            eventValue: newIsOpen ? 'Opened' : 'Closed',
        });
    };

    const handleOptionClick = (option: IIconMenuHorizontalDropdownOption) => {
        option.handleClick();
        setIsOpen(false);
        props.onToggle?.(false);

        trackingService.track({ eventCategory: props.track.eventCategory, eventAction: `${props.track.eventAction}OptionClicked`, eventValue: option.label });
    };

    useEffect(() => {
        if (!props.isOpen) {
            return;
        }

        setIsOpen(props.isOpen);
    }, [props.isOpen]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownReference.current && !dropdownReference.current.contains(event.target as Node)) {
                setIsOpen(false);
                props.onToggle?.(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, props]);

    return (
        <div className={props.containerClassName} ref={dropdownReference}>
            <IconButton
                className={props.className}
                disabled={props.disabled}
                handleClick={toggleDropdown}
                tooltip={props.tooltip}
                testId={props.testId}
                tooltipPlacement="top-start"
                track={{ eventCategory: props.track.eventCategory, eventAction: `${props.track.eventAction}ToggleClicked` }}
            >
                {props.icon}
            </IconButton>
            {isOpen && (
                <div className={props.menuClassName}>
                    {props.options.map((option) => (
                        <button key={option.key} type="button" className={props.itemClassName} onClick={() => handleOptionClick(option)}>
                            {option.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default IconMenuHorizontalDropdown;
